import styles from "./Projects.module.css";

import { PiDownloadSimpleBold } from "react-icons/pi";
import { RiAdvertisementFill } from "react-icons/ri";
import { BiSolidPurchaseTag } from "react-icons/bi";
import { FaGithub, FaSteam } from "react-icons/fa";
import { FiExternalLink } from "react-icons/fi";

import {
  multiplayerGame,
  billboardNetwork,
  nineLives,
  cluckingChaos,
  levelEditor,
  duckBurger,
  brunoComedy,
  nftTerrains,
  redImpact,
  boids,
  area51,
  cyberSec,
  interactiveMap,
  bravado,
  microMiners,
  icky,
  encryptedPackets,
} from "./projects/projects";

import { useEffect, useState } from "react";
import { FiClock } from "react-icons/fi";
import { PiStarFour, PiGameController, PiFolderOpen } from "react-icons/pi";

const featuredProjectTitles = new Set([
  "Multiplayer Networking",
  "React Three Fiber Level Editor",
  "Duck vs Burger - Fully On-Chain Game in Downstream",
  "Billboard Advertisement Network",
  "Clucking Chaos - Casual Mobile Game",
  "Bruno's Comedy Club - LLM AI Automated Live Comedy",
]);

const allProjects = [
  multiplayerGame,
  levelEditor,
  duckBurger,
  billboardNetwork,
  cluckingChaos,
  brunoComedy,
  encryptedPackets,
  nineLives,
  nftTerrains,
  redImpact,
  boids,
  area51,
  cyberSec,
  interactiveMap,
  bravado,
  icky,
  microMiners,
];

const featuredProjects = allProjects.filter((project) =>
  featuredProjectTitles.has(project.title)
);

const archivedProjects = allProjects.filter(
  (project) => !featuredProjectTitles.has(project.title)
);

const projectYears = allProjects
  .map((project) => Number.parseInt(project.date, 10))
  .filter((year) => !Number.isNaN(year));
const earliestProjectYear = Math.min(...projectYears);
const latestProjectYear = Math.max(...projectYears);

const projectStats = [
  {
    label: "Projects",
    value: `${allProjects.length} selected builds`,
    icon: PiFolderOpen,
  },
  {
    label: "Timespan",
    value: `${earliestProjectYear} to ${latestProjectYear}`,
    icon: FiClock,
  },
  {
    label: "Published games",
    value: `${allProjects.filter((project) => project.links?.steam).length} shipped to Steam`,
    icon: PiGameController,
  },
];

function Installs({ amount }) {
  return (
    <div className={styles.installs}>
      <PiDownloadSimpleBold /> {amount} installs
    </div>
  );
}

function Impressions({ amount }) {
  return (
    <div className={styles.installs}>
      <RiAdvertisementFill /> {amount} impressions/month
    </div>
  );
}

function AmountBought({ amount }) {
  return (
    <div className={styles.installs}>
      <BiSolidPurchaseTag /> {amount} ads bought
    </div>
  );
}

function Sales({ amount }) {
  return (
    <div className={styles.installs}>
      <BiSolidPurchaseTag /> {amount} units sold
    </div>
  );
}

function LinkMore({ onSeeMore }) {
  return (
    <button
      type="button"
      className={styles.link}
      onClick={onSeeMore}
    >
      <span>See More</span>
      <FiExternalLink />
    </button>
  );
}

function LinkGitHub({ link }) {
  return (
    <a
      className={styles.link}
      href={link}
      target="_blank"
      rel="noopener noreferrer"
    >
      <FaGithub />
      <span>GitHub</span>
      <FiExternalLink />
    </a>
  );
}

function LinkSteam({ link }) {
  return (
    <a
      className={styles.link}
      href={link}
      target="_blank"
      rel="noopener noreferrer"
    >
      <FaSteam />
      <span>Steam Page</span>
      <FiExternalLink />
    </a>
  );
}

function Links({ links = {}, onSeeMore }) {
  const { steam, github } = links;
  if (!onSeeMore && !steam && !github) return null;

  return (
    <div className={styles.linksRow}>
      {onSeeMore && <LinkMore onSeeMore={onSeeMore} />}
      {github && <LinkGitHub link={github} />}
      {steam && <LinkSteam link={steam} />}
    </div>
  );
}

function Achievements({
  achievements: { installs, impressions, customers, sales },
}) {
  if (!installs && !impressions && !customers && !sales) return null;
  return (
    <div className={styles.achievementsRow}>
      {installs && <Installs amount={installs} />}
      {impressions && <Impressions amount={impressions} />}
      {customers && <AmountBought amount={customers} />}
      {sales && <Sales amount={sales} />}
    </div>
  );
}

function Skills({ skills }) {
  if (!skills || skills.length === 0) return null;
  return (
    <div className={styles.skillsRow}>
      {skills.map((skill, index) => (
        <div key={index} className={styles.skill}>
          {skill}
        </div>
      ))}
    </div>
  );
}

function getProjectLabel(data) {
  if (data.links?.steam) return "Published";
  if (data.achievements?.impressions || data.achievements?.installs) return "Commercial impact";
  if (data.skills?.includes("OpenAI API") || data.skills?.includes("LLM")) return "AI";
  if (data.skills?.includes("Solidity") || data.skills?.includes("Ethereum")) return "Web3";
  if (data.skills?.includes("React") || data.skills?.includes("Three.js")) return "Web app";
  return "Interactive";
}

function Project({ data, onSeeMore, compact = false }) {
  const label = getProjectLabel(data);

  return (
    <div className={`${styles.project} ${compact ? styles.compactProject : ""}`}>
      <div className={styles.flexThumbnail}>
        <img src={data.thumbnail} alt={`${data.title} thumbnail`} />
      </div>
      <div className={styles.flexContent}>
        <div className={styles.projectLabel}>{label}</div>
        <div className={styles.contentHeader}>
          <div className={styles.title}>{data.title}</div>
          <div className={styles.date}>{data.date}</div>
        </div>

        <div className={styles.description}>
          {data.description}
          {(data.links || onSeeMore) && (
            <Links onSeeMore={onSeeMore} links={data.links} />
          )}
        </div>

        {data.achievements && <Achievements achievements={data.achievements} />}
        {data.skills && <Skills skills={data.skills} />}
      </div>
    </div>
  );
}

function Overlay({ activeProject, closeOverlay }) {
  function handleBackgroundClick(e) {
    if (e.target === e.currentTarget) {
      closeOverlay();
    }
  }

  return (
    <div className={styles.overlay} onClick={handleBackgroundClick}>
      <div className={styles.overlayContent}>
        <button
          type="button"
          className={styles.closeButton}
          onClick={closeOverlay}
        >
          ×
        </button>
        <div className={styles.overlayHeader}>
          <div className={styles.flexThumbnail}>
            <img
              src={activeProject.thumbnail}
              alt={`${activeProject.title} thumbnail`}
            />
          </div>
          <div className={styles.overlayHeaderDetails}>
            <div className={styles.title}>{activeProject.title}</div>
            <div className={styles.date}>{activeProject.date}</div>
            <Skills skills={activeProject.skills} />
            <Links links={activeProject.links} />
          </div>
        </div>
        <div className={styles.overlayBody}>
          {activeProject.content && activeProject.content()}
        </div>
      </div>
    </div>
  );
}

function Projects() {
  const [activeProject, setActiveProject] = useState(null);

  useEffect(() => {
    if (activeProject) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [activeProject]);

  function openOverlay(project) {
    setActiveProject(project);
  }

  function closeOverlay() {
    setActiveProject(null);
  }

  return (
    <div className={styles.projects}>
      <p className={styles.intro}>
        These are a mix of some of my personal and professional projects I've
        worked on over the years.
      </p>
      <div className={styles.stats}>
        {projectStats.map(({ label, value, icon: Icon }) => (
          <div key={label} className={styles.statCard}>
            <Icon className={styles.statIcon} />
            <div className={styles.statLabel}>{label}</div>
            <div className={styles.statValue}>{value}</div>
          </div>
        ))}
      </div>

      <div className={styles.subsection}>
        <div className={styles.subsectionHeader}>
          <PiStarFour className={styles.subsectionIcon} />
          <div>
            <div className={styles.subsectionTitle}>Featured work</div>
            <div className={styles.subsectionDescription}>
              A few projects that best represent product thinking, technical depth,
              and shipped outcomes.
            </div>
          </div>
        </div>
        <div className={styles.featuredList}>
          {featuredProjects.map((project) => (
            <Project
              key={project.title}
              data={project}
              onSeeMore={project.content ? () => openOverlay(project) : undefined}
            />
          ))}
        </div>
      </div>

      <div className={styles.subsection}>
        <div className={styles.subsectionHeader}>
          <PiFolderOpen className={styles.subsectionIcon} />
          <div>
            <div className={styles.subsectionTitle}>Selected archive</div>
            <div className={styles.subsectionDescription}>
              Earlier shipped games, experiments, and smaller technical builds.
            </div>
          </div>
        </div>
        <div className={styles.archiveGrid}>
          {archivedProjects.map((project) => (
            <Project
              key={project.title}
              data={project}
              compact
              onSeeMore={project.content ? () => openOverlay(project) : undefined}
            />
          ))}
        </div>
      </div>

      {activeProject && (
        <Overlay activeProject={activeProject} closeOverlay={closeOverlay} />
      )}
    </div>
  );
}

export default Projects;
